import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Avatar,
  Button,
  TextField,
  Chip,
  Fade,
  Zoom,
  Divider,
  Stack,
  Snackbar,
} from '@mui/material';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const Careers = () => {
  const [email, setEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const responsibilities = [
    "Recruiting banking staff",
    "Training banking staff",
    "Supervising banking staff",
    "Appraising banking staff",
    "Promoting Diamond Card services"
  ];

  const benefits = [
    { icon: <TrendingUpIcon />, title: "Career Growth", desc: "Fast-track promotion opportunities" },
    { icon: <SchoolIcon />, title: "Learning & Development", desc: "Continuous professional training" },
    { icon: <PeopleIcon />, title: "Global Network", desc: "Work with international teams" },
    { icon: <PublicIcon />, title: "Global Mobility", desc: "Opportunity to work abroad" },
  ];

  const handleSubscribe = () => {
    if (email) {
      setSnackbar({ open: true, message: 'Successfully subscribed! We\'ll notify you when positions become available.', severity: 'success' });
      setEmail('');
    }
  };

  return (
    <Box>
      {/* Hero Section - Increased Height with Reduced Overlay Opacity */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 12, md: 16 },
               minHeight: { xs: 450, md: 700},
          color: 'white', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: 'url("/images/productspageherobg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 3,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '4rem' }, 
                  mb: 2, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: -0.5,
                }}
              >
                CAREERS
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ color: colors.premiumGold, maxWidth: 600, mx: 'auto', fontWeight: 500, letterSpacing: 1 }}
              >
                Join the Diamond Card Corporation Team
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Role Description - Premium */}
          <Grid item xs={12} md={7}>
            <Zoom in timeout={500}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 4, height: '100%', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: colors.premiumGold, width: 55, height: 55, mr: 2 }}>
                    <WorkIcon sx={{ fontSize: 30, color: colors.navy }} />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.navy, fontSize: '1.6rem' }}>
                    Customer Service Role
                  </Typography>
                </Box>
                
                <Typography variant="body1" paragraph sx={{ fontSize: '0.95rem', lineHeight: 1.7, color: colors.textDark }}>
                  Diamond Card Corporation personnel act in a customer service role, advising on and assisting with 
                  services such as transaction enquiries, statement history, authorising loans and moving money.
                </Typography>
                
                <Divider sx={{ my: 2.5 }} />
                
                <Typography variant="subtitle1" gutterBottom sx={{ color: colors.premiumGold, fontWeight: 600, mb: 1.5 }}>
                  Typical responsibilities of the job include:
                </Typography>
                
                <List dense>
                  {responsibilities.map((item, idx) => (
                    <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon sx={{ color: colors.premiumGold, fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item} 
                        primaryTypographyProps={{ sx: { fontWeight: 500, color: colors.textDark, fontSize: '0.85rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Zoom>
          </Grid>

          {/* Current Vacancies Status - Premium */}
          <Grid item xs={12} md={5}>
            <Zoom in timeout={700}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: colors.navy, color: 'white', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, bgcolor: `${colors.premiumGold}0D`, borderRadius: '50%' }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, position: 'relative', zIndex: 1 }}>
                  <Avatar sx={{ bgcolor: colors.premiumGold, width: 55, height: 55, mr: 2 }}>
                    <WarningIcon sx={{ fontSize: 30, color: colors.navy }} />
                  </Avatar>
                  <Typography variant="h4" sx={{ color: colors.premiumGold, fontWeight: 700, fontSize: '1.6rem' }}>
                    Current Status
                  </Typography>
                </Box>
                
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2.5, 
                    bgcolor: `${colors.premiumGold}1A`, 
                    borderRadius: 2,
                    '& .MuiAlert-icon': { color: colors.premiumGold }
                  }}
                >
                  <AlertTitle sx={{ color: colors.premiumGold, fontWeight: 700 }}>No Vacancies Available</AlertTitle>
                  Diamond Card Corporation does not currently have any vacancies.
                </Alert>
                
                <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontSize: '0.85rem' }}>
                  All vacancies will be advertised on this page. Please check back regularly for updates on future opportunities.
                </Typography>
                
                <Box sx={{ mt: 3, p: 2.5, bgcolor: `${colors.premiumGold}0D`, borderRadius: 3 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.premiumGold, mb: 1.5, fontSize: '0.85rem' }}>
                    <EmailIcon sx={{ fontSize: 16 }} />
                    Get notified when positions become available
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField 
                      placeholder="Your email address" 
                      size="small"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={{ 
                        bgcolor: 'white', 
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                      }}
                      fullWidth
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleSubscribe}
                      sx={{ 
                        bgcolor: colors.premiumGold, 
                        color: colors.navy, 
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem',
                        '&:hover': { bgcolor: colors.lightGold }
                      }}
                    >
                      Notify Me
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>

        {/* Why Join Us Section - 4 cards in one row */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h3" textAlign="center" sx={{ fontWeight: 700, color: colors.navy, mb: 2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Why Join Diamond Card?
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 5, color: colors.textLight, fontSize: '0.9rem' }}>
            Discover the benefits of being part of our prestigious team
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {benefits.map((item, idx) => (
              <Zoom in timeout={400 + idx * 200} key={idx}>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3, 
                    transition: 'all 0.3s', 
                    borderTop: `2px solid ${colors.premiumGold}`, 
                    '&:hover': { 
                      transform: 'translateY(-6px)', 
                      boxShadow: `0 15px 30px ${colors.premiumGold}30`,
                    } 
                  }}>
                    <Avatar sx={{ bgcolor: colors.premiumGold, width: 55, height: 55, mx: 'auto', mb: 1.5 }}>
                      {item.icon}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy, mb: 0.5, fontSize: '0.95rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {item.desc}
                    </Typography>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Box>

        {/* Employee Testimonial Section */}
        <Box sx={{ mt: 8, bgcolor: colors.pearl, p: 4, borderRadius: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.navy, mb: 1.5, fontStyle: 'italic', fontSize: '1rem' }}>
                "Working at Diamond Card has been transformative for my career. The culture of excellence and innovation is unmatched."
              </Typography>
              <Typography variant="body2" sx={{ color: colors.premiumGold, fontWeight: 600 }}>
                — Senior Executive, New York Office
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Chip 
                icon={<PeopleIcon />} 
                label="500+ Employees Worldwide" 
                sx={{ bgcolor: colors.navy, color: colors.premiumGold, fontWeight: 600, p: 1.5 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Company Culture Section - 3 stats in one row */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: colors.navy, mb: 3, fontSize: '1.6rem' }}>
            Our Culture
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Paper sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                bgcolor: colors.premiumGold, 
                borderRadius: 3,
                width: '100%',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 25px ${colors.premiumGold}40` }
              }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: colors.navy, fontSize: '2rem' }}>15+</Typography>
                <Typography variant="body2" sx={{ color: colors.navy, fontWeight: 600, fontSize: '0.8rem' }}>Countries of Operation</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Paper sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                bgcolor: colors.navy, 
                borderRadius: 3,
                width: '100%',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 25px ${colors.premiumGold}40` }
              }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: colors.premiumGold, fontSize: '2rem' }}>24/7</Typography>
                <Typography variant="body2" sx={{ color: colors.premiumGold, fontWeight: 600, fontSize: '0.8rem' }}>Global Support</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Paper sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                bgcolor: colors.premiumGold, 
                borderRadius: 3,
                width: '100%',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 25px ${colors.premiumGold}40` }
              }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: colors.navy, fontSize: '2rem' }}>100%</Typography>
                <Typography variant="body2" sx={{ color: colors.navy, fontWeight: 600, fontSize: '0.8rem' }}>Commitment to Excellence</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Diamond Network Footer */}
      <Box sx={{ bgcolor: colors.premiumGold, py: 3.5, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, letterSpacing: 4, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
          DIAMOND NETWORK
        </Typography>
        <Typography variant="caption" sx={{ color: colors.navy, opacity: 0.7, mt: 0.5, display: 'block', letterSpacing: 1.5 }}>
          Building the future of banking together
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ bgcolor: colors.navy, color: colors.premiumGold }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Careers;